<script>
import { mdiAccountGroupOutline } from '@mdi/js'
import gql from 'graphql-tag'
import DashboardSection from './dashboard_section.vue'

export default {
    name: 'OrganizationSection',
    components: { DashboardSection },
    data() {
        return {
            organization: null,
            loading: true,
            error: '',
            requestId: 0,
            activeContext: null
        }
    },
    computed: {
        authReady() {
            return !!this.$auth && !this.$auth.loading && this.$auth.isAuthenticated === true && !!this.$auth.user?.id
        },
        isMember() {
            // The helper is also used by the organizations route guard. Never call it before auth is ready.
            return this.authReady && typeof this.$auth.userInOrganization === 'function' &&
                this.$auth.userInOrganization()
        },
        authContext() {
            const user = this.$auth?.user
            return JSON.stringify([this.authReady, this.isMember, user?.id, user?.org?.branchId, user?.org?.name])
        },
        isAdmin() {
            return !!this.organization?.admins.some((admin) => admin?.accountId === this.$auth?.user?.id)
        },
        members() {
            return (this.organization?.accounts || []).filter((member) => member?.account && member.accountId)
        },
        section() {
            const org = this.organization
            const rows = []
            if (org) {
                rows.push({
                    id: org.orgId,
                    title: org.organizationName,
                    detail: 'Your account organization',
                    status: this.isAdmin ? 'Admin' : 'Member',
                    tone: 'neutral'
                })
                const pending = org.accounts.filter((member) => member && !member.account).length
                const disabled = this.members.filter((member) => member.isAccountLocked).length
                rows.push({
                    id: 'members',
                    title: `${this.members.length} members`,
                    detail: `${pending} pending invitations · ${disabled} disabled accounts`,
                    status: 'User accounts',
                    tone: 'neutral'
                })
                // The source page exposes child organizations only to enterprise organization admins.
                if (this.isAdmin && org.enterprise) {
                    rows.push({
                        id: 'branches',
                        title: `${org.childrenOrg.length} direct branches`,
                        detail: org.childrenOrg.map((branch) => branch.organizationName).join(' · ') ||
                            'No sub-organizations exist under this organization.',
                        status: 'Sub-organizations',
                        tone: 'neutral'
                    })
                }
            }
            return {
                id: 'organization',
                title: 'Organization',
                icon: mdiAccountGroupOutline,
                description: 'Stay connected to the people and devices in your organization.',
                link: this.isMember && org ? '/your-account/organizations' : '/help-support',
                action: this.isMember && org ? this.isAdmin ? 'Manage organization' : 'View organization' : 'Contact support',
                rows,
                footnote: this.loading ? 'Waiting for account organization details…' : this.error ?
                    'Organization details are temporarily unavailable' : org ?
                    'Current organization only · Member count excludes pending invitations' :
                    'Organization access is managed by your organization administrator'
            }
        },
        emptyText() {
            return this.isMember ?
                'No organization was found for your account. Contact your organization administrator or Getac Support to confirm your membership.' :
                'Your account is not linked to an organization. Ask your organization administrator for an invitation to join, or contact Getac Support for help.'
        }
    },
    watch: {
        authContext: {
            immediate: true,
            handler() {
                this.fetchOrganization()
            }
        }
    },
    beforeDestroy() {
        this.requestId++
    },
    methods: {
        emitSummary() {
            this.$emit('navigation', this.section.link)
            this.$emit('summary', {
                metric: this.loading ? '…' : this.error || !this.organization ? '—' :
                    String(this.members.length).padStart(2, '0'),
                metricLabel: this.loading ? 'Organization members' : this.error ? 'Organization unavailable' :
                    this.organization ? 'Organization members' : this.isMember ? 'No organization found' :
                    'Not in an organization'
            })
        },
        async fetchOrganization() {
            const context = this.authContext
            if (this.activeContext === context) return
            const requestId = ++this.requestId
            this.organization = null
            this.error = ''
            this.activeContext = null
            this.loading = !this.authReady
            if (!this.isMember) {
                this.emitSummary()
                return
            }
            this.activeContext = context
            this.loading = true
            this.emitSummary()
            const isCurrent = () => requestId === this.requestId && context === this.authContext
            try {
                // Resolve the account's own base organization, not a guessed root/branch ID from auth claims.
                const baseResult = await this.$apollo.query({
                    query: gql`
                        query ResolveBaseAccountOrganization {
                            resolveBaseAccountOrganization {
                                orgId
                            }
                        }
                    `,
                    fetchPolicy: 'network-only'
                })
                if (!isCurrent()) return
                const base = baseResult.data?.resolveBaseAccountOrganization
                if (base === null) return
                if (!base?.orgId) throw new Error('Base organization unavailable')
                const { data } = await this.$apollo.query({
                    query: gql`
                        query ResolveAccountOrganization($resolveAccountOrganizationId: String) {
                            resolveAccountOrganization(id: $resolveAccountOrganizationId) {
                                orgId
                                organizationName
                                enterprise
                                accounts {
                                    account { email }
                                    accountId
                                    admin
                                    requestedEmail
                                    isAccountLocked
                                }
                                admins { accountId }
                                childrenOrg { orgId organizationName }
                            }
                        }
                    `,
                    variables: { resolveAccountOrganizationId: base.orgId },
                    fetchPolicy: 'network-only'
                })
                if (!isCurrent()) return
                const org = data?.resolveAccountOrganization
                if (org === null) return
                if (!org?.organizationName || org.orgId !== base.orgId || !Array.isArray(org.accounts) ||
                    !Array.isArray(org.admins)) throw new Error('Organization unavailable')
                const admin = org.admins.some((member) => member?.accountId === this.$auth.user.id)
                if (admin && org.enterprise && (!Array.isArray(org.childrenOrg) ||
                    org.childrenOrg.some((branch) => !branch?.orgId || !branch.organizationName))) {
                    throw new Error('Branches unavailable')
                }
                this.organization = org
            } catch (error) {
                if (isCurrent()) this.error = 'Your organization could not be loaded. Please try again.'
            } finally {
                if (isCurrent()) {
                    this.activeContext = null
                    this.loading = false
                    this.emitSummary()
                }
            }
        }
    }
}
</script>

<template>
    <dashboard-section
        :section="section"
        :loading="loading"
        :error="error"
        loading-text="Loading your account organization…"
        :empty-text="emptyText"
        @retry="fetchOrganization"
    />
</template>
